import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import {
  FOOTAGE_AI_CLIP_SEC,
  FOOTAGE_AI_PROMPT_MAX_LENGTH,
  type FootageAiStyle,
  type FootagePeople,
} from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { AiClipError } from '../../doc/musicVideoErrors';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { sectionAt } from '../../timeline/timelineGeometry';
import { AiLookControls } from './AiLookControls';
import { AiReferences } from './AiReferences';
import { AiStyleControl } from './AiStyleControl';
import type { AiClipRequest } from './useAiClip';
import { useAiShotOptions } from './useAiShotOptions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ERROR_MESSAGES: Partial<Record<AiClipError, (typeof footageMessages)[keyof typeof footageMessages]>> = {
  busy: footageMessages.aiErrorBusy,
  later: footageMessages.aiErrorLater,
  demo: footageMessages.aiErrorDemo,
  failed: footageMessages.aiErrorFailed,
  references: footageMessages.aiErrorReferences,
};

interface AiClipFormProps {
  make: (request: AiClipRequest) => Promise<boolean>;
  isBusy: boolean;
  error: AiClipError | null;
}

/**
 * The form of a new AI clip (design music-video h1): the text, written from the song and the section
 * until the user changes it, the style chips, the people, the cost and the balance, "Make the clip".
 *
 * With a cast library (directed video design i1 to i3) the form gets references (max 3) and a style:
 * in a cut that a directed video made, the cast of the section and the style switch of the video
 * (no chips and no people: the references and the style say them); in any other cut, optional
 * references and an optional style text. The price stays `plan.aiClipCredits`.
 */
const AiClipFormComponent: React.FC<AiClipFormProps> = ({ make, isBusy, error }) => {
  const intl = useIntl();
  const { data, songMap, sectionIndex, transport, readOnly, cutUuid } = useMusicVideoEditor();
  const section = useMemo(
    () =>
      songMap.sections.find((item) => item.index === sectionIndex) ??
      sectionAt(songMap.sections, transport.positionSec),
    [sectionIndex, songMap.sections, transport.positionSec]
  );
  const shot = useAiShotOptions(data, cutUuid, section);
  const isDirected = shot.directed !== null;
  const defaultPrompt =
    section === null || section.mood === null
      ? intl.formatMessage(footageMessages.aiDefaultPromptSong, { title: data.song.title })
      : intl.formatMessage(footageMessages.aiDefaultPrompt, {
          mood: section.mood,
          section: section.label,
          title: data.song.title,
        });
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [isEdited, setIsEdited] = useState(false);
  const [style, setStyle] = useState<FootageAiStyle>('film');
  const [people, setPeople] = useState<FootagePeople>('none');
  useEffect(() => {
    if (!isEdited) {
      setPrompt(defaultPrompt);
    }
  }, [defaultPrompt, isEdited]);
  const credits = data.plan.aiClipCredits;
  const left = data.plan.creditsLeft;
  const errorMessage = error === null ? undefined : ERROR_MESSAGES[error];
  const question = isDirected ? footageMessages.aiShotQuestion : footageMessages.aiQuestion;

  return (
    <>
      <div className={styles.sectionHead}>
        <b>
          {section === null
            ? intl.formatMessage(isDirected ? footageMessages.aiShotHeadingSong : footageMessages.aiHeadingSong, {
                title: data.song.title,
              })
            : intl.formatMessage(isDirected ? footageMessages.aiShotHeading : footageMessages.aiHeading, {
                section: section.label,
              })}
        </b>
        {section?.mood !== null && section?.mood !== undefined && <small>{section.mood}</small>}
      </div>
      <span className={styles.eyebrow}>{intl.formatMessage(question)}</span>
      <textarea
        className={styles.textArea}
        value={prompt}
        maxLength={FOOTAGE_AI_PROMPT_MAX_LENGTH}
        aria-label={intl.formatMessage(question)}
        onChange={(event) => {
          setPrompt(event.target.value);
          setIsEdited(true);
        }}
      />
      {!isDirected && (
        <AiLookControls
          style={style}
          onStyle={setStyle}
          people={people}
          onPeople={setPeople}
          styleChipsOff={shot.effectiveStyleText !== ''}
        />
      )}
      {shot.hasLibrary && (
        <AiReferences
          isDirected={isDirected}
          fileIds={shot.fileIds}
          onChange={shot.setFileIds}
          library={data.castLibrary}
          directedCast={shot.directed?.cast ?? []}
          disabled={readOnly}
        />
      )}
      {shot.directed !== null && shot.directed.styleText !== '' && (
        <AiStyleControl
          kind="directed"
          directedStyleText={shot.directed.styleText}
          isOn={shot.isStyleOn}
          onToggle={shot.toggleStyle}
          disabled={readOnly}
        />
      )}
      {shot.directed === null && shot.hasLibrary && (
        <AiStyleControl kind="text" value={shot.styleText} onChange={shot.setStyleText} disabled={readOnly} />
      )}
      <div className={styles.costBox}>
        <div className={styles.costLine}>
          <span>{intl.formatMessage(footageMessages.aiSpec, { seconds: FOOTAGE_AI_CLIP_SEC })}</span>
          <span>{intl.formatMessage(footageMessages.aiCost, { credits })}</span>
        </div>
        <div className={clsx(styles.costLine, styles.costLineMuted)}>
          <span>{intl.formatMessage(footageMessages.aiHave)}</span>
          <span>
            {left === null
              ? intl.formatMessage(footageMessages.aiHaveNoLimit)
              : intl.formatMessage(footageMessages.aiCost, { credits: left })}
          </span>
        </div>
      </div>
      <SMLButton
        size="sm"
        omitCap
        stretch
        isLoading={isBusy}
        disabled={isBusy || readOnly || prompt.trim() === ''}
        onClick={() => {
          make({ prompt, style, people, ...shot.request }).catch(console.error);
        }}
      >
        {intl.formatMessage(isDirected ? footageMessages.aiShotMake : footageMessages.aiMake, { credits })}
      </SMLButton>
      {errorMessage !== undefined && <p className={styles.errorText}>{intl.formatMessage(errorMessage)}</p>}
      <p className={styles.hint}>
        {intl.formatMessage(shot.hasLibrary ? footageMessages.aiSamePrice : footageMessages.aiHint)}
      </p>
    </>
  );
};

export const AiClipForm = TypedMemo(AiClipFormComponent);
